import { effect, inject, Injectable, untracked } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ActiveCompanyService } from './active-company.service';
import { PermissionsService } from '@/app/core/permissions/permissions.service';

// ══════════════════════════════════════════════════════════════════════════
// ŞİRKET DEĞİŞİNCE AÇIK SAYFAYI YENİLER
//
// ── ÖNCEKİ HÂL VE İKİ SORUNU ──────────────────────────────────────────────
// Eskiden yalnız SEKTÖREL bir sayfadaysa panoya atılıyordu:
//
//   1. Sektörel OLMAYAN sayfalar (Çalışma Alanım, Web Sitem, Tahsilat &
//      Faturalama, CRM…) yerinde kalıyor ve verisi YENİLENMİYORDU. Bu
//      sayfaların çoğu veriyi `ngOnInit`'te bir kez çekiyor; şirket değişse de
//      ekranda ÖNCEKİ şirketin kayıtları duruyordu. Hiçbir hata görünmüyor,
//      kullanıcı yanlış şirketin verisine bakıyor.
//
//   2. Sektörel sayfada modül KONTROL EDİLMEDEN panoya atılıyordu: yeni şirket
//      o modüle sahip olsa bile kullanıcı sayfadan atılıyordu.
//
// ── ŞİMDİ ─────────────────────────────────────────────────────────────────
// Şirket değişince aynı adrese YENİDEN gidiliyor. Bu tek hamle iki işi de
// yapıyor, çünkü yol üzerindeki `portalGuard` zaten "bu sayfa bu şirkette var
// mı?" sorusunu yanıtlıyor (ROUTE_MODULE_MAP + hasModule):
//   • modül yoksa  → guard panoya yönlendirir,
//   • modül varsa  → bileşen yeniden kurulur, ngOnInit yeni şirketin
//                    verisini çeker.
//
// ── NEDEN YETKİLER BEKLENİYOR ─────────────────────────────────────────────
// `permissions.refresh()` bir HTTP çağrısı. Şirket değişir değişmez
// yönlendirseydik guard ESKİ şirketin modül listesiyle karar verirdi:
// yeni şirkette var olan bir sayfadan atılmak ya da olmayan bir sayfada
// kalmak. İkisi de sessiz. Bu yüzden `loading` true→false geçişi beklenir.
// ══════════════════════════════════════════════════════════════════════════

/**
 * Bileşeni gerçekten yeniden kurmak için sıçranan ara adres.
 *
 * Angular aynı adrese gidildiğinde bileşeni YENİDEN KULLANIR; `ngOnInit`
 * çalışmaz ve veri tazelenmez. Araya bir adres girip geri dönmek bileşeni
 * yıkıp yeniden kurar. `skipLocationChange` sayesinde tarayıcı geçmişinde iz
 * bırakmaz.
 */
const RELOAD_BOUNCE = '/portal/_reload';

// Bu prefix'lerden biriyle başlayan route'lar sektörel modül sayfasıdır.
// Artık yalnız "yönlendir" listesi DEĞİL: guard modül kontrolünü kendi
// yaptığı için burası sadece belgeleme amaçlı tutuluyor ve başka yerlerden
// (SECTORAL_MODULE_CODES) kullanılıyor.
export const SECTORAL_PREFIXES = [
  '/portal/personalsite',
  '/portal/fitness',
  '/portal/beauty',
  '/portal/hotel',
  '/portal/fnb',
  '/portal/education',
  '/portal/health',
  '/portal/law',
  '/portal/realestate',
  '/portal/booking',
  '/portal/site',
  '/portal/storage',
  '/portal/ngo',
  '/portal/religious',
  '/portal/consulting',
  '/portal/eventorg',
  '/portal/homeservices',
  '/portal/construction',
  '/portal/realty',
  '/portal/agency',
  '/portal/agriculture',
  '/portal/creator',
  '/portal/sportsclub',
  '/portal/logistics',
  '/portal/automotive',
  '/portal/daycare',
  '/portal/petservices',
  '/portal/restaurant',
  '/portal/hotelmgmt',
  '/portal/edumgmt',
  '/portal/lawmgmt',
  '/portal/immigration',
  '/portal/clinic',
  '/portal/pharmacy',
  '/portal/dental',
  '/portal/insurance',
  // İnsan Kaynakları & İşe Alım (modül kodu 'hr', rota öneki de 'hr').
  '/portal/hr',
];

/** Sektörel modül kodları (web sitesi/web-builder ile gelen) — prefix listesinden türetilir. */
export const SECTORAL_MODULE_CODES = new Set(SECTORAL_PREFIXES.map(p => p.replace('/portal/', '')));

@Injectable({ providedIn: 'root' })
export class CompanyRouteWatcher {
  private router = inject(Router);
  private activeCompany = inject(ActiveCompanyService);
  private permissions = inject(PermissionsService);

  private previousId: number | null | undefined = undefined;

  /** Şirket değişti, yetkiler tazelenince yenileme yapılacak. */
  private pending = false;

  /** `loading` true→false geçişini yakalamak için önceki değer. */
  private prevLoading = false;

  constructor() {
    effect(() => {
      const currentId = this.activeCompany.activeCompanyId();
      const loading = this.permissions.loading();

      // İlk evaluation (boot anındaki snapshot) — navigate etme, sadece kaydet.
      if (this.previousId === undefined) {
        this.previousId = currentId;
        this.prevLoading = loading;
        return;
      }

      if (currentId !== this.previousId) {
        this.previousId = currentId;

        // Şirket seçimi kaldırıldıysa beklenecek yetki tazelemesi yok:
        // hiçbir şirkete bağlı olmayan bir sayfada kalmanın anlamı yok.
        if (currentId === null) {
          this.pending = false;
          this.prevLoading = loading;
          untracked(() => void this.router.navigate(['/portal/dashboard']));
          return;
        }

        this.pending = true;
      }

      const bittiSimdi = this.prevLoading && !loading;
      this.prevLoading = loading;

      if (this.pending && bittiSimdi) {
        this.pending = false;
        untracked(() => this.reloadCurrentRoute());
      }
    });
  }

  /**
   * Açık sayfayı yeniden kurar. Guard yol üzerinde tekrar çalışır; yeni
   * şirkette sayfa yoksa panoya yönlendirir.
   */
  private reloadCurrentRoute(): void {
    const url = this.router.url;

    // Panodayken yenilemeye gerek yok: pano zaten şirkete duyarlı ve
    // sıçrama gereksiz bir titremeye yol açardı.
    if (url.split('?')[0] === '/portal/dashboard') return;

    void this.router
      .navigateByUrl(RELOAD_BOUNCE, { skipLocationChange: true })
      .then(() => this.router.navigateByUrl(url))
      .catch(() => {
        // Sıçrama başarısız olursa kullanıcıyı yanlış şirketin verisiyle
        // baş başa bırakmaktansa panoya al.
        void this.router.navigate(['/portal/dashboard']);
      });
  }
}

/** Sıçrama adresi — portal rotalarında karşılığı tanımlıdır. */
export const COMPANY_RELOAD_BOUNCE_PATH = RELOAD_BOUNCE;
